import "./tournament-sponsorship-tier-create-edit.scss";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import {
  GSEmptyList,
  GSForm,
  GSSidePanelPage,
} from "golfstatus_react_components";
import {
  faArrowRight,
  faChevronLeft,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  clearNotifications,
  createTournamentSponsorshipTier,
  getSponsorshipTierSponsors,
  getTournamentSponsorshipTier,
  saveTournamentSponsorshipTier,
  selectCurrentSponsorshipTier,
  selectCurrentSponsorshipTierSponsors,
  selectLoading,
  selectSponsorshipTierList,
  selectSponsorshipTierNotifications,
  setCurrentSponsorshipTier,
  setCurrentSponsorshipTierSponsors,
} from "../../../reducers/sponsorshipTierSlice";
import {
  getSaveBannerActions,
  useNotificationBanner,
  useNotificationNavigation,
} from "../../../hooks/notificationHooks";
import { useFormDataValidation } from "../../../hooks/formHooks";
import {
  selectCurrentTournament,
  selectTiersAvailable,
} from "../../../reducers/tournamentSlice";
import {
  editSponsorshipTierForm,
  getTierPaidMessage,
} from "../../../forms/SponsorshipTierForm";
import { getNotificationItemInfo } from "../../../helpers/Converters";
import { clearNotifications as clearSponsorNotifications } from "../../../reducers/sponsorSlice";

const TournamentSponsorshipTierCreateEdit = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const matchAdd = useMatch("/:tournamentID/sponsors/sponsorship-tiers/add");
  const matchEdit = useMatch(
    "/:tournamentID/sponsors/sponsorship-tiers/:sponsorshipTierID/edit"
  );

  const sponsorshipTierNotifications = useSelector(
    selectSponsorshipTierNotifications
  );
  const currentSponsorshipTier = useSelector(selectCurrentSponsorshipTier);
  const currentTournament = useSelector(selectCurrentTournament);
  const tiersAvailable = useSelector(selectTiersAvailable);
  const tournamentSponsorshipTiers = useSelector(selectSponsorshipTierList);
  const currentSponsorshipTierSponsors = useSelector(
    selectCurrentSponsorshipTierSponsors
  );
  const tierLoading = useSelector(selectLoading);

  const [context, isValid, setIsValid] = useFormDataValidation({
    initialValid: false,
    data: currentSponsorshipTier,
    setData: setCurrentSponsorshipTier,
  });

  useEffect(() => {
    if (
      matchEdit?.params?.sponsorshipTierID &&
      matchEdit?.params?.sponsorshipTierID !== currentSponsorshipTier.id
    ) {
      dispatch(
        getTournamentSponsorshipTier(matchEdit?.params?.sponsorshipTierID)
      );
    }
    if (matchAdd) {
      dispatch(setCurrentSponsorshipTier({}));
    }
  }, [
    dispatch,
    currentSponsorshipTier.id,
    matchEdit?.params?.sponsorshipTierID,
    setIsValid,
    matchAdd,
  ]);

  useEffect(() => {
    if (matchEdit) {
      dispatch(
        getSponsorshipTierSponsors(matchEdit?.params?.sponsorshipTierID)
      );
      if (matchAdd) {
        dispatch(setCurrentSponsorshipTierSponsors([]));
      }
    }
  }, [dispatch, matchAdd, matchEdit]);

  context.currentSponsorshipTierSponsors = currentSponsorshipTierSponsors;
  context.tierLoading = tierLoading;

  context.manageSponsors = () => {
    dispatch(clearNotifications());
    dispatch(clearSponsorNotifications());
    navigate("manage-sponsors");
  };

  context.getActions = () => {
    let actions = [
      {
        title: "Manage Sponsors",
        rightIcon: faArrowRight,
        actionClick: context.manageSponsors,
        type: "grey mobile-icon no-wrap",
      },
    ];
    return actions;
  };

  context.getEmptyMessage = () => {
    return (
      <GSEmptyList
        title={`${currentSponsorshipTier.name}`}
        detail="This tier doesn't have any sponsors yet."
      />
    );
  };
  context.getTechEmptyMessage = () => {
    return (
      <GSEmptyList
        title={`${currentSponsorshipTier.name}`}
        detail="This tournament does not have a technology sponsor."
      />
    );
  };

  const getNavigation = () => {
    return {
      title: `Sponsor Tier Details`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const saveSponsorshipTier = () => {
    setIsValid(false);
    setUnsaved(false);
    let payload = { ...currentSponsorshipTier };

    if (matchAdd) {
      payload.sponsorshipTierType = "custom";
      payload.tournament = { ...currentTournament };
      payload.sortOrder = tournamentSponsorshipTiers.length - 1;

      dispatch(createTournamentSponsorshipTier(payload));
    }
    if (matchEdit) {
      dispatch(saveTournamentSponsorshipTier(payload));
    }
  };

  const remove = () => {
    navigate(`../sponsorship-tiers/${currentSponsorshipTier.id}/remove`);
  };

  const getDrawerActions = () => {
    let actions = [
      {
        name: matchAdd ? "Save & Continue" : "Save",
        isDisabled: !isValid,
        action: saveSponsorshipTier,
        type: "black",
      },
      { name: "Cancel", action: leftNavigation, type: "grey" },
    ];

    if (currentSponsorshipTier.sponsorshipTierType === "custom") {
      actions.push({
        name: "Delete Tier",
        action: remove,
        type: "transparent red no-wrap",
      });
    }

    return actions;
  };

  const leftNavigation = () => {
    navigate(-1);
  };

  const getContent = () => {
    let title = matchAdd ? "Add Sponsor Tier" : "Edit Sponsor Tier";
    if (!tiersAvailable && !tierLoading.length > 0) {
      return getTierPaidMessage(title);
    }
    return (
      <GSForm
        formTitle={title}
        formSections={editSponsorshipTierForm(context, currentSponsorshipTier)}
      ></GSForm>
    );
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  const getDefaultBanner = () => {
    if (currentSponsorshipTier.sponsorshipTierType === "default") {
      return {
        title: getNotificationItemInfo(
          `This is the default tier. Sponsors will be automatically moved, if added to another tier.`,
          faExclamationCircle
        ),
        state: "grey",
      };
    }
    if (currentSponsorshipTier.sponsorshipTierType === "technology") {
      return {
        title: getNotificationItemInfo(
          `This tier can only display the Technology Sponsor.`,
          faExclamationCircle
        ),
        state: "grey",
      };
    }
  };

  let notificationSettings = {
    notifications: [...sponsorshipTierNotifications],
    bannerActions: getSaveBannerActions(saveSponsorshipTier, leftNavigation),
    timeoutAction,
    defaultBanner: tiersAvailable ? getDefaultBanner() : null,
  };

  useNotificationNavigation(
    "saved",
    sponsorshipTierNotifications,
    `../sponsorship-tiers/[id]/edit`,
    "New Sponsorship Tier"
  );

  const [bannerNotifications, setUnsaved] = useNotificationBanner(
    notificationSettings
  );

  return (
    <tournament-sponsorship-tier-create-edit>
      <GSSidePanelPage
        header={getNavigation()}
        banner={bannerNotifications}
        content={getContent()}
        drawer={tiersAvailable ? getDrawer() : []}
      ></GSSidePanelPage>
    </tournament-sponsorship-tier-create-edit>
  );
};

export default TournamentSponsorshipTierCreateEdit;
